import type { FunctionComponent } from "react";
import { useEffect, useMemo, useState } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, usePaginationFragment, usePreloadedQuery } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { DefaultPageSize } from "scmp-app/components/search/consts";
import type { ArticleResultIdMapEntry } from "scmp-app/components/search/contexts";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { query } from "scmp-app/components/search/search-result-panel";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { useInfiniteScrollTriggerReference } from "scmp-app/lib/hooks";
import { Typeform } from "scmp-app/lib/typeform";
import type { searchResultListSearchResultsQuery$key } from "scmp-app/queries/__generated__/searchResultListSearchResultsQuery.graphql";
import type { searchResultPanelQuery } from "scmp-app/queries/__generated__/searchResultPanelQuery.graphql";

import { DateRangeMenu } from "./advanced-filter-options/date-range-menu";
import { PlusCheckbox } from "./advanced-filter-options/plus-checkbox";
import { SectionMenu } from "./advanced-filter-options/section-menu";
import { TypeMenu } from "./advanced-filter-options/type-menu";
import { ArticleSearchResultList } from "./article-search-result-list";
import { SectionSearchResultList } from "./section-search-result-list";
import {
  AdvancedFilterOptionsContainer,
  Container,
  FilterWrapper,
  HeaderContainer,
  NoArticleResultsContent,
  NoResultsContainer,
  NoResultsContent,
  NoResultsHeader,
  ResultAndSortingContainer,
  ResultText,
  StyledLoading,
  StyledSearchResultListSortingMenu,
  TotalCount,
  TotalResultText,
} from "./styles";
import { TopicSearchResultList } from "./topic-search-result-list";
import { VideoSectionSearchResultList } from "./video-section-search-result-list";

export type Props = {
  className?: string;
  reference: PreloadedQuery<searchResultPanelQuery>;
};

export const SearchResultList: FunctionComponent<Props> = ({ className, reference }) => {
  const queryReference = usePreloadedQuery(query, reference);

  const {
    data: results,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<searchResultPanelQuery, searchResultListSearchResultsQuery$key>(
    graphql`
      fragment searchResultListSearchResultsQuery on Query
      @argumentDefinitions(
        after: { type: "String" }
        articleTypeIds: { type: "[String!]" }
        contentTypes: { type: "[ContentTypeEnum!]" }
        endDate: { type: "Timestamp" }
        first: { type: "Int", defaultValue: 10 }
        orderBy: { type: "ArticlesSearchOrderByInput" }
        paywallTypeIds: { type: "[String!]" }
        query: { type: "String!" }
        sectionIds: { type: "[String!]" }
        startDate: { type: "Timestamp" }
        variant: { type: "Int", defaultValue: 0 }
      )
      @refetchable(queryName: "searchArticleResultPaginationQuery") {
        articleSearch(
          filter: {
            articleTypes: $articleTypeIds
            contentTypes: $contentTypes
            endDate: $endDate
            paywallTypeIds: $paywallTypeIds
            query: $query
            sectionIds: $sectionIds
            startDate: $startDate
            variant: $variant
          }
          orderBy: $orderBy
          first: $first
          after: $after
        ) @connection(key: "articleSearchResultListQuery__articleSearch") {
          resultId
          totalCount
          edges {
            node {
              content {
                entityId
              }
            }
            ...articleSearchResultListArticleSearchEdge
          }
          ...hooksTypeMenuArticleSearchConnection
          ...hooksSectionMenuArticleSearchConnection
        }
        sectionSearch(first: 10, filter: { query: $query }) {
          edges {
            ...sectionSearchResultListSectionSearchEdge
          }
        }
        topicSearch(first: 10, filter: { query: $query }) {
          edges {
            ...topicSearchResultListTopicSearchEdge
          }
        }
        videoSectionSearch(first: 10, filter: { query: $query }) {
          edges {
            ...videoSectionSearchResultListVideoSectionSearchEdge
          }
        }
      }
    `,
    queryReference,
  );

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => loadNext(DefaultPageSize),
  });

  const noResult = useMemo(
    () =>
      [
        notEmpty(results.articleSearch?.edges),
        notEmpty(results.topicSearch?.edges),
        notEmpty(results.sectionSearch?.edges),
        notEmpty(results.videoSectionSearch?.edges),
      ].every(edgeLength => !edgeLength),
    [
      results.articleSearch?.edges,
      results.sectionSearch?.edges,
      results.topicSearch?.edges,
      results.videoSectionSearch?.edges,
    ],
  );

  const { articleResultIdMap, currentValue, initialSearchId, setInitialSearchId } =
    useSearchContext();

  const [isSentSysEvent, setIsSentSysEvent] = useState(false);

  useEffect(() => {
    if (isSentSysEvent || initialSearchId === undefined) return;
    sendGA4Tracking({
      action: "sys",
      category: "search",
      customized_parameters: {
        search_id: initialSearchId,
        search_text: currentValue,
      },
      subcategory: "result",
    } as SearchEvent);
    setIsSentSysEvent(true);
  }, [currentValue, initialSearchId, isSentSysEvent]);

  useEffect(() => {
    const data = results.articleSearch?.edges ?? [];
    const resultId = results.articleSearch?.resultId;
    if (!notEmpty(data) || !notEmpty(articleResultIdMap)) return;

    setInitialSearchId?.(initial => initial ?? resultId ?? "");

    const page = Math.ceil(articleResultIdMap.size / DefaultPageSize) + 1;

    data.forEach(({ node }, index) => {
      const entityId = node.content?.entityId;
      if (!entityId || articleResultIdMap.has(entityId)) return;

      const position = index - (page - 1) * DefaultPageSize + 1;
      articleResultIdMap.set(entityId, {
        page,
        position,
        resultId,
      } satisfies ArticleResultIdMapEntry);
    });
  }, [
    articleResultIdMap,
    results.articleSearch?.edges,
    results.articleSearch?.resultId,
    setInitialSearchId,
  ]);

  if (noResult) {
    return (
      <NoResultsContainer>
        <NoResultsHeader>Sorry! There are no results for your search term.</NoResultsHeader>
        <NoResultsContent>
          Please check if your spelling is correct, or try a different word or phrase.
        </NoResultsContent>
      </NoResultsContainer>
    );
  }

  return (
    <Container className={className}>
      <Typeform configKey="SEARCH_RESULT" />
      <HeaderContainer>
        <AdvancedFilterOptionsContainer>
          <FilterWrapper>
            <DateRangeMenu />
            {results.articleSearch && (
              <>
                <SectionMenu reference={results.articleSearch} />
                <TypeMenu reference={results.articleSearch} />
              </>
            )}
          </FilterWrapper>
          <PlusCheckbox />
        </AdvancedFilterOptionsContainer>
        <ResultAndSortingContainer>
          <TotalResultText>
            <TotalCount>{results.articleSearch?.totalCount?.toLocaleString()}</TotalCount>
            {" results "}
            for <ResultText>&ldquo;{currentValue}&rdquo;</ResultText>
          </TotalResultText>
          <StyledSearchResultListSortingMenu />
        </ResultAndSortingContainer>
      </HeaderContainer>

      <SectionSearchResultList reference={results.sectionSearch?.edges ?? []} />
      <VideoSectionSearchResultList reference={results.videoSectionSearch?.edges ?? []} />
      <TopicSearchResultList reference={results.topicSearch?.edges ?? []} />

      {notEmpty(results.articleSearch?.totalCount) ? (
        <>
          <ArticleSearchResultList reference={results.articleSearch?.edges ?? []} />
          {hasNextPage && (
            <StyledLoading
              ref={element => infiniteScrollTriggerReference(isLoading ? null : element)}
            />
          )}
        </>
      ) : (
        <NoArticleResultsContent>No results</NoArticleResultsContent>
      )}
    </Container>
  );
};

SearchResultList.displayName = "SearchResultList";
